/**
 * DiscountApprovalsPage — admin queue for proposed Customer_Aux changes.
 *
 * Sales OR accounting admin proposes a change → counter-party reviews and
 * either approves or rejects. Super-admins can self-approve directly.
 *
 * Route: /admin/discount-approvals
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";

const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
};

export default function DiscountApprovalsPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [decideOn, setDecideOn] = useState(null);
  const [decideNote, setDecideNote] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/discount-approvals", {
        params: statusFilter ? { status: statusFilter, limit: 200 } : { limit: 200 },
      });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load approvals");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const decide = async (decision) => {
    if (!decideOn?.id) return;
    try {
      await api.post(`/admin/discount-approvals/${decideOn.id}/decide`,
        { decision, note: decideNote });
      toast.success(t("discount_approval.decided", null, { decision }));
      setDecideOn(null);
      setDecideNote("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-7xl mx-auto">
        <button data-testid="approvals-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("aux.back_home_tooltip")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Handshake size={20} weight="fill" className="text-emerald-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="approvals-title">
          {t("discount_approval.page_title")}
        </h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <p className="text-sm text-slate-600 max-w-3xl">{t("discount_approval.subtitle")}</p>

        <div className="flex flex-wrap gap-2 text-sm" data-testid="approvals-filters">
          {[
            ["pending", t("discount_approval.filter_pending")],
            ["approved", t("discount_approval.filter_approved")],
            ["rejected", t("discount_approval.filter_rejected")],
            ["", t("discount_approval.filter_all")],
          ].map(([v, l]) => (
            <button key={v} data-testid={`approvals-filter-${v || "all"}`}
              onClick={() => setStatusFilter(v)}
              className={`px-3 py-1.5 rounded border ${statusFilter === v
                ? "bg-emerald-600 text-white border-emerald-600"
                : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="bg-white border rounded-md overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="approvals-table">
            <thead className="bg-slate-50 border-b text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">{t("discount_approval.col_company")}</th>
                <th className="text-left px-3 py-2">{t("discount_approval.col_submitter")}</th>
                <th className="text-left px-3 py-2">{t("discount_approval.col_changes")}</th>
                <th className="text-left px-3 py-2">{t("discount_approval.col_status")}</th>
                <th className="text-left px-3 py-2">{t("discount_approval.col_created")}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={6} className="p-6 text-center text-sm text-slate-500">{t("aux.loading")}</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-slate-500">{t("discount_approval.empty")}</td></tr>
              )}
              {!loading && items.map(it => (
                <tr key={it.id} data-testid={`approvals-row-${it.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold">{it.company_name || it.customer_id}</div>
                    <div className="text-xs text-slate-500 font-mono">{it.customer_id}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{it.submitter_email}</div>
                    <div className="text-slate-500 uppercase">{it.submitter_role}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <Diff cur={it.current} pro={it.proposed} />
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_TONE[it.status] || "bg-slate-100"}`}>
                      {it.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {it.created_at ? new Date(it.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {it.status === "pending" && (
                      <button data-testid={`approvals-decide-${it.id}`}
                        onClick={() => setDecideOn(it)}
                        className="text-xs text-emerald-700 hover:text-emerald-900 font-bold">
                        {t("discount_approval.decide")}
                      </button>
                    )}
                    {it.status !== "pending" && it.decided_by && (
                      <div className="text-[11px] text-slate-500">
                        <div>{it.decided_by}</div>
                        <div className="italic">{it.decision_note || "—"}</div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {decideOn && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-md shadow-xl max-w-lg w-full p-5" data-testid="approvals-decide-modal">
            <h3 className="text-lg font-bold mb-1">
              {t("discount_approval.decide_title", null, { name: decideOn.company_name || decideOn.customer_id })}
            </h3>
            <div className="text-xs text-slate-500 mb-3">
              {t("discount_approval.decide_intro", null, { submitter: decideOn.submitter_email })}
            </div>
            <div className="border rounded p-3 bg-slate-50 mb-3">
              <Diff cur={decideOn.current} pro={decideOn.proposed} />
            </div>
            {decideOn.note && (
              <div className="border-l-4 border-blue-300 bg-blue-50 px-3 py-2 mb-3 text-xs">
                <span className="font-bold text-blue-900 uppercase tracking-wider text-[10px] block">
                  {t("discount_approval.submitter_note")}
                </span>
                {decideOn.note}
              </div>
            )}
            <textarea data-testid="approvals-decide-note"
              value={decideNote} onChange={e => setDecideNote(e.target.value)}
              rows={3} placeholder={t("discount_approval.decide_note_placeholder")}
              className="w-full border rounded px-2 py-1.5 text-sm mb-3" />
            <div className="flex justify-end gap-2">
              <button data-testid="approvals-decide-cancel"
                onClick={() => { setDecideOn(null); setDecideNote(""); }}
                className="px-3 py-2 text-sm border rounded">
                {t("aux.cancel")}
              </button>
              <button data-testid="approvals-decide-reject"
                onClick={() => decide("rejected")}
                className="px-4 py-2 bg-red-600 text-white rounded font-bold text-sm hover:bg-red-700">
                {t("discount_approval.reject")}
              </button>
              <button data-testid="approvals-decide-approve"
                onClick={() => decide("approved")}
                className="px-4 py-2 bg-emerald-600 text-white rounded font-bold text-sm hover:bg-emerald-700">
                {t("discount_approval.approve")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Diff({ cur = {}, pro = {} }) {
  // Render the multiplier as "% OFF" for D / B / Spare consistent with
  // CustomerAuxPage. Net-X for payment term, $ for limit.
  const pct = (m) => (!m || m <= 0 ? "—" : `${Math.round((1 - m) * 100)}%`);
  const lines = [
    ["D-series", pct(cur.discount_d_series), pct(pro.discount_d_series)],
    ["B-series", pct(cur.discount_b_series), pct(pro.discount_b_series)],
    ["Spare", pct(cur.discount_spare_part), pct(pro.discount_spare_part)],
    ["Net (days)", `Net-${cur.payment_term_days || 0}`, `Net-${pro.payment_term_days || 0}`],
    ["Open Limit", `$${(cur.open_account_limit || 0).toLocaleString()}`,
      `$${(pro.open_account_limit || 0).toLocaleString()}`],
  ];
  return (
    <div className="space-y-0.5">
      {lines.map(([k, a, b]) => {
        const changed = a !== b;
        return (
          <div key={k} className={`flex gap-2 ${changed ? "font-bold text-amber-900" : "text-slate-500"}`}>
            <span className="w-20">{k}:</span>
            <span>{a}</span>
            <span>→</span>
            <span>{b}</span>
          </div>
        );
      })}
    </div>
  );
}
